import { useState, useEffect, useCallback, useRef } from "react";
import { Seo } from "@/components/Seo";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trash2, UploadCloud, ImageIcon, Copy, Check, Heart } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { findExcerpt } from "@/data/excerpts";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "art", label: "Art" },
  { key: "books", label: "Books" },
];

export default function GalleryPage({
  only = null,
  overline = "The Gallery",
  heading = "Art & Books",
  subtitle = "A living collection of artwork and published books. Explore the pieces below.",
  note = null,
  collectionTag = null,
  noteAction = null,
}) {
  const { user } = useAuth();
  const isAdmin = user && user.role;
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(only || "all");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState(only || "art");
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [size, setSize] = useState("");
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);

  const [wishlist, setWishlist] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("ddl_wishlist") || "[]")); } catch { return new Set(); }
  });
  const [showWishlist, setShowWishlist] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showSample, setShowSample] = useState(false);
  const [wlName, setWlName] = useState("");
  const [wlEmail, setWlEmail] = useState("");
  const [wlSending, setWlSending] = useState(false);

  const toggleWishlist = (id) => {
    setWishlist((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        toast.success("Added to your wishlist");
      }
      try { localStorage.setItem("ddl_wishlist", JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const wishlistItems = items.filter((i) => wishlist.has(i.id));
  const wishlistTotal = wishlistItems.reduce((s, i) => s + (i.price || 0), 0);

  const submitWishlist = async () => {
    if (!wlEmail.includes("@") || !wlEmail.split("@")[1]?.includes(".")) {
      toast.error("Please enter a valid email");
      return;
    }
    setWlSending(true);
    try {
      await api.post("/wishlist", { name: wlName, email: wlEmail, items: [...wishlist] });
      toast.success("Wishlist sent! Dr. Louden will email you to complete your order.");
      setWishlist(new Set());
      try { localStorage.removeItem("ddl_wishlist"); } catch {}
      setShowWishlist(false);
      setWlName("");
      setWlEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not send wishlist. Please try again.");
    } finally {
      setWlSending(false);
    }
  };

  const EMAIL = "Deltadlenterprise2026@gmail.com";

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = EMAIL;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* ignore */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Email copied to clipboard.");
    setTimeout(() => setCopied(false), 2000);
  };

  const load = useCallback(async () => {
    try {
      const url = only ? `/gallery?category=${only}` : "/gallery";
      const { data } = await api.get(url);
      setItems(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [only]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);
  const masonry = only !== "books";

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Please choose an image to upload.");
      return;
    }
    const fd = new FormData();
    fd.append("category", category);
    fd.append("title", title);
    if (price) fd.append("price", price);
    if (size) fd.append("size", size);
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/gallery", fd);
      toast.success("Image uploaded.");
      setTitle("");
      setPrice("");
      setSize("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Removed.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const handleEditPrice = async (item) => {
    const current = item.price != null ? String(item.price) : "";
    const next = window.prompt(`Set price for "${item.title || item.category}" (blank to clear):`, current);
    if (next === null) return; // cancelled
    try {
      const { data } = await api.patch(`/gallery/${item.id}`, { price: next.trim() });
      const newPrice = data.price ?? null;
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, price: newPrice } : i)));
      toast.success("Price updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update price.");
    }
  };

  const fmtPrice = (p) =>
    p == null || p === "" ? null : `$${Number(p).toFixed(2)}`;

  const handleBuy = async (item) => {
    try {
      const { data } = await api.post("/checkout/session", {
        item_id: item.id,
        quantity: 1,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout.");
    }
  };

  return (
    <section
      id={only || "gallery"}
      data-testid={only ? `page-${only}` : "page-gallery"}
      className="bg-[#050505] px-6 md:px-12 pt-36 pb-24 md:py-28"
    >
      {only === null && (
        <Seo
          title="Art & Books Gallery — Delta DL Enterprise, LLC"
          description="A living collection of artwork and published books from The Delta DL Enterprise, LLC Collection."
          path="/gallery"
        />
      )}
      <div className="mx-auto max-w-[1600px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="text-[10px] uppercase tracking-[0.24em] text-[#7C3AED] sm:text-xs sm:tracking-[0.4em]">{overline}</span>
          {collectionTag && (
            <span
              data-testid={`gallery-collection-tag-${only || "all"}`}
              className="mt-4 inline-flex w-fit rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/10 px-4 py-2 text-[10px] uppercase tracking-[0.28em] text-[#C4B5FD]"
            >
              {collectionTag}
            </span>
          )}
          <h1 className="mt-4 font-display text-6xl md:text-8xl uppercase tracking-tight text-white">
            {heading}
          </h1>
          <p className="mt-6 max-w-xl text-lg text-white/60">
            {subtitle}
          </p>

          <div
            data-testid="gallery-inquiries"
            className="mt-8 inline-flex flex-wrap items-center gap-3 rounded-full border border-white/15 bg-white/[0.03] px-4 py-2"
          >
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 pl-2">For inquiries</span>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="gallery-email"
              className="text-sm md:text-base text-white hover:text-[#7C3AED] transition-colors select-all"
            >
              {EMAIL}
            </a>
            <button
              type="button"
              data-testid="gallery-email-copy"
              onClick={copyEmail}
              aria-label="Copy email address"
              className="magnetic-btn inline-flex items-center gap-1.5 rounded-full bg-[#7C3AED] px-3 py-1.5 text-xs uppercase tracking-widest text-white hover:bg-white hover:text-black"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </motion.div>

        {/* Admin upload panel */}
        {isAdmin && (
          <form
            data-testid="gallery-upload-form"
            onSubmit={handleUpload}
            className="mt-12 rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-8"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/50">
              <UploadCloud size={16} /> Admin Upload
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-4 md:items-end">
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/40 mb-2">Category</label>
                {only ? (
                  <div className="line-input flex items-center text-white/80 capitalize">{only}</div>
                ) : (
                  <select
                    data-testid="gallery-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="line-input cursor-pointer"
                  >
                    <option value="art" className="bg-[#111]">Art</option>
                    <option value="books" className="bg-[#111]">Books</option>
                    <option value="apparel" className="bg-[#111]">Apparel</option>
                  </select>
                )}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/40 mb-2">Title (optional)</label>
                <input
                  data-testid="gallery-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Sunrise Study"
                  className="line-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/40 mb-2">Price ($)</label>
                <input
                  data-testid="gallery-price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="e.g. 24.99"
                  inputMode="decimal"
                  className="line-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/40 mb-2">Size (optional)</label>
                <input
                  data-testid="gallery-size"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  placeholder='e.g. 24" × 36" canvas'
                  className="line-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/40 mb-2">Image file</label>
                <input
                  data-testid="gallery-file"
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="block w-full text-sm text-white/70 file:mr-4 file:rounded-full file:border-0 file:bg-[#7C3AED] file:px-4 file:py-2 file:text-white file:cursor-pointer"
                />
              </div>
              <button
                data-testid="gallery-upload-btn"
                type="submit"
                disabled={uploading}
                className="magnetic-btn rounded-full bg-[#7C3AED] px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white hover:bg-white hover:text-black disabled:opacity-50"
              >
                {uploading ? "Uploading…" : "Upload"}
              </button>
            </div>
          </form>
        )}

        {/* Filters */}
        {!only && (
          <div className="mt-12 flex gap-3">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`gallery-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`rounded-full px-5 py-2 text-xs uppercase tracking-widest transition-colors ${
                  filter === f.key
                    ? "bg-[#7C3AED] text-white"
                    : "border border-white/20 text-white/60 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Client note */}
        {note && (
          <motion.div
            data-testid={`gallery-note-${only || "all"}`}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-10 flex flex-col gap-4 rounded-xl border-l-2 border-[#7C3AED] bg-white/[0.03] px-6 py-5 sm:flex-row sm:items-center sm:justify-between"
          >
            <p className="max-w-2xl text-sm md:text-base leading-relaxed text-white/70">{note}</p>
            {noteAction ? (
              <a
                href={noteAction.href}
                data-testid={`gallery-note-action-${only || "all"}`}
                className="magnetic-btn inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-[#7C3AED] px-6 py-3 text-xs md:text-sm font-semibold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-black sm:self-auto"
              >
                {noteAction.label}
              </a>
            ) : (
              <a
                href={`mailto:${EMAIL}`}
                data-testid={`gallery-note-email-${only || "all"}`}
                className="magnetic-btn inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-[#7C3AED]/50 px-5 py-2 text-xs md:text-sm font-semibold tracking-wide text-[#7C3AED] transition-colors hover:bg-[#7C3AED] hover:text-white sm:self-auto"
              >
                {EMAIL}
              </a>
            )}
          </motion.div>
        )}

        {/* Grid */}
        <div className="mt-10">
          {loading ? (
            <p className="text-white/40">Loading…</p>
          ) : filtered.length === 0 ? (
            <div
              data-testid="gallery-empty"
              className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/15 py-24 text-center"
            >
              <ImageIcon size={40} className="text-white/20" />
              <p className="mt-4 text-white/50">No pieces here yet.</p>
              {isAdmin && <p className="mt-1 text-sm text-white/30">Use the upload panel above to add art or books.</p>}
            </div>
          ) : (
            <div
              id={only ? `${only}-collection` : "collection"}
              className={masonry ? "columns-1 gap-6 sm:columns-2 lg:columns-3" : "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"}
            >
              {filtered.map((item, i) => {
                const isBook = item.category === "books";
                const info = (
                  <>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">{item.category}</span>
                    {item.title && <p className="text-white text-sm mt-0.5 line-clamp-2">{item.title}</p>}
                    {item.description && <p className="mt-1 text-xs leading-relaxed text-white/55 line-clamp-3">{item.description}</p>}
                    {fmtPrice(item.price) && (
                      <div className="mt-2 flex flex-col gap-2">
                        <p
                          data-testid={`gallery-price-${item.id}`}
                          className="font-display text-lg tracking-wide text-white"
                        >
                          {fmtPrice(item.price)}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <a
                            data-testid={`gallery-request-${item.id}`}
                            href={`mailto:${EMAIL}?subject=${encodeURIComponent(
                              `Purchase Request: ${item.title || item.category}`
                            )}&body=${encodeURIComponent(
                              `Hello, I would like to purchase "${item.title || "this item"}" (${fmtPrice(item.price)}). Please contact me to complete my order.\n\nMy name:\nMy phone:`
                            )}`}
                            className="inline-flex items-center gap-2 rounded bg-[#7C3AED] px-4 py-1.5 text-[10px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#6D28D9]"
                          >
                            Request to Purchase
                          </a>
                        </div>
                      </div>
                    )}
                  </>
                );
                return (
                <motion.div
                  key={item.id}
                  data-testid={`gallery-item-${item.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: (i % 6) * 0.05 }}
                  onClick={() => setSelected(item)}
                  role="button"
                  aria-label={`View details for ${item.title || item.category}`}
                  className={`group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] cursor-pointer ${
                    masonry ? "mb-6 break-inside-avoid" : ""
                  }`}
                >
                  <img
                    src={`${API}/gallery/${item.id}/image`}
                    alt={item.title || item.category}
                    loading="lazy"
                    className={
                      isBook
                        ? "w-full aspect-[2/3] bg-[#0b0b0f] object-contain p-4 transition-transform duration-700 group-hover:scale-105"
                        : `w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                            masonry ? "h-auto" : "aspect-[2/3] object-center"
                          }`
                    }
                  />
                  {item.badge && (
                    <span
                      data-testid={`gallery-badge-${item.id}`}
                      className="absolute left-3 top-3 rounded-full bg-[#7C3AED] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white shadow-lg"
                    >
                      {item.badge}
                    </span>
                  )}
                  {!isBook && (
                    <button
                      data-testid={`gallery-wishlist-${item.id}`}
                      onClick={(e) => { e.stopPropagation(); toggleWishlist(item.id); }}
                      aria-label={wishlist.has(item.id) ? "Remove from wishlist" : "Add to wishlist"}
                      className={`absolute right-3 top-3 rounded-full p-2 shadow-lg transition-colors ${
                        wishlist.has(item.id)
                          ? "bg-[#7C3AED] text-white"
                          : "bg-black/60 text-white/70 hover:text-white"
                      }`}
                    >
                      <Heart className="h-4 w-4" fill={wishlist.has(item.id) ? "currentColor" : "none"} />
                    </button>
                  )}
                  {!isBook && (
                    <div className="border-t border-white/10 px-4 py-3 text-center">
                      {item.title && (
                        <p
                          data-testid={`gallery-title-below-${item.id}`}
                          className="truncate text-sm font-medium tracking-wide text-white/85"
                        >
                          {item.title.replace(" — Dr. Dennis Louden", "")}
                        </p>
                      )}
                      {fmtPrice(item.price) && (
                        <span
                          data-testid={`gallery-price-below-${item.id}`}
                          className="mt-0.5 block font-display text-xl tracking-wide text-white"
                        >
                          {fmtPrice(item.price)}
                        </span>
                      )}
                      {item.size && (
                        <p
                          data-testid={`gallery-size-below-${item.id}`}
                          className="mt-0.5 text-xs tracking-wide text-white/50"
                        >
                          {item.size}
                        </p>
                      )}
                    </div>
                  )}
                  {isAdmin && (
                    <div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        data-testid={`gallery-edit-price-${item.id}`}
                        onClick={() => handleEditPrice(item)}
                        aria-label="Edit price"
                        className="rounded-full bg-black/70 px-3 py-2 text-[10px] uppercase tracking-widest text-white/80 hover:bg-[#7C3AED] hover:text-white"
                      >
                        Price
                      </button>
                      <button
                        data-testid={`gallery-delete-${item.id}`}
                        onClick={() => handleDelete(item.id)}
                        aria-label="Delete image"
                        className="rounded-full bg-black/70 p-2 text-white/80 hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Book detail modal */}
      {selected && (
        <div
          data-testid="book-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-[#0b0b0f]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid md:grid-cols-2">
              <div className="overflow-hidden">
                <img
                  src={`${API}/gallery/${selected.id}/image`}
                  alt={selected.title || selected.category}
                  className="h-72 w-full object-cover md:h-full md:min-h-[420px]"
                />
              </div>
              <div className="flex flex-col p-6 md:p-8">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">{selected.category}</span>
                  {selected.badge && (
                    <span data-testid="book-modal-badge" className="rounded-full bg-[#7C3AED] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                      {selected.badge}
                    </span>
                  )}
                </div>
                <h3 data-testid="book-modal-title" className="mt-2 font-display text-2xl tracking-wide text-white">
                  {selected.title}
                </h3>
                {selected.size && (
                  <p data-testid="book-modal-size" className="mt-1.5 text-xs uppercase tracking-[0.25em] text-white/45">
                    {selected.size}
                  </p>
                )}
                {selected.description && (
                  <p data-testid="book-modal-description" className="mt-3 text-sm leading-relaxed text-white/60">
                    {selected.description}
                  </p>
                )}
                {fmtPrice(selected.price) && (
                  <p data-testid="book-modal-price" className="mt-4 font-display text-3xl tracking-wide text-white">
                    {fmtPrice(selected.price)}
                  </p>
                )}
                <div className="mt-6 flex flex-wrap gap-3">
                  {fmtPrice(selected.price) && (
                    <a
                      data-testid="book-modal-request"
                      href={`mailto:${EMAIL}?subject=${encodeURIComponent(
                        `Purchase Request: ${selected.title || selected.category}`
                      )}&body=${encodeURIComponent(
                        `Hello, I would like to purchase "${selected.title || "this item"}" (${fmtPrice(selected.price)}). Please contact me to complete my order.\n\nMy name:\nMy phone:`
                      )}`}
                      className="inline-flex items-center gap-2 rounded-full bg-[#7C3AED] px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#6D28D9]"
                    >
                      Request to Purchase
                    </a>
                  )}
                  <button
                    data-testid="book-modal-wishlist"
                    onClick={() => toggleWishlist(selected.id)}
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black"
                  >
                    <Heart className="h-3.5 w-3.5" fill={wishlist.has(selected.id) ? "currentColor" : "none"} />
                    {wishlist.has(selected.id) ? "In Your Wishlist" : "Add to Wishlist"}
                  </button>
                  {selected.category === "books" && findExcerpt(selected.title) && (
                    <button
                      data-testid="book-modal-sample"
                      onClick={() => setShowSample(true)}
                      className="inline-flex items-center gap-2 rounded-full border border-[#C9A227]/50 px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-[#C9A227] transition-colors hover:bg-[#C9A227] hover:text-black"
                    >
                      Read a Free Sample
                    </button>
                  )}
                </div>
                <button
                  data-testid="book-modal-close"
                  onClick={() => setSelected(null)}
                  className="mt-auto pt-6 text-left text-xs uppercase tracking-widest text-white/50 transition-colors hover:text-white"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Book sample reader */}
      {selected && showSample && (
        <div
          data-testid="book-sample-overlay"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setShowSample(false)}
        >
          <div
            className="w-full max-w-2xl rounded-2xl border border-[#C9A227]/30 bg-[#0b0b0f] p-8 md:p-10"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9A227]">Free Sample</span>
            <h3 data-testid="book-sample-title" className="mt-2 font-display text-2xl tracking-wide text-white">
              {selected.title}
            </h3>
            <p className="mt-2 text-xs italic text-white/40">
              Sample preview — the opening of this book.
            </p>
            <p data-testid="book-sample-text" className="mt-6 text-sm leading-loose text-white/75 md:text-base">
              {findExcerpt(selected.title)}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                data-testid="book-sample-close"
                onClick={() => setShowSample(false)}
                className="rounded-full border border-white/25 px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black"
              >
                Back to Book
              </button>
              <span className="text-xs text-white/40">Enjoyed it? Request the full book from the book details.</span>
            </div>
          </div>
        </div>
      )}

      {/* Wishlist tray */}
      {wishlistItems.length > 0 && (
        <div
          data-testid="wishlist-bar"
          className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-4 rounded-full border border-[#7C3AED]/50 bg-[#0b0b0f]/95 px-6 py-3 shadow-2xl backdrop-blur"
        >
          <span className="text-sm text-white/80">
            Wishlist: <span className="font-semibold text-white">{wishlistItems.length}</span>{" "}
            item{wishlistItems.length > 1 ? "s" : ""}
            {wishlistTotal > 0 && <span className="text-white/60"> · ${wishlistTotal.toFixed(2)}</span>}
          </span>
          <button
            data-testid="wishlist-request-button"
            onClick={() => setShowWishlist(true)}
            className="rounded-full bg-[#7C3AED] px-5 py-2 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#6D28D9]"
          >
            Request These Items
          </button>
        </div>
      )}

      {/* Wishlist modal */}
      {showWishlist && (
        <div
          data-testid="wishlist-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setShowWishlist(false)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-white/10 bg-[#0b0b0f] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-xl tracking-wide text-white">Send Your Wishlist</h3>
            <ul className="mt-4 max-h-40 space-y-1 overflow-y-auto text-sm text-white/70">
              {wishlistItems.map((i) => (
                <li key={i.id} className="flex justify-between gap-3">
                  <span className="line-clamp-1">{i.title}</span>
                  <span>{i.price ? `$${i.price.toFixed(2)}` : ""}</span>
                </li>
              ))}
            </ul>
            <input
              data-testid="wishlist-name-input"
              value={wlName}
              onChange={(e) => setWlName(e.target.value)}
              placeholder="Your name"
              className="mt-4 w-full rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 text-sm text-white placeholder-white/30 focus:border-[#7C3AED] focus:outline-none"
            />
            <input
              data-testid="wishlist-email-input"
              type="email"
              value={wlEmail}
              onChange={(e) => setWlEmail(e.target.value)}
              placeholder="Your email *"
              className="mt-3 w-full rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 text-sm text-white placeholder-white/30 focus:border-[#7C3AED] focus:outline-none"
            />
            <button
              data-testid="wishlist-submit-button"
              onClick={submitWishlist}
              disabled={wlSending}
              className="mt-4 w-full rounded-lg bg-[#7C3AED] py-2.5 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#6D28D9] disabled:opacity-50"
            >
              {wlSending ? "Sending…" : "Send Wishlist Request"}
            </button>
            <p className="mt-3 text-center text-xs text-white/40">
              Dr. Louden will email you personally to complete your order.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
